import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {ErrorHandler, inject, NgModule, provideAppInitializer, provideZoneChangeDetection} from "@angular/core";
import {BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig} from "@angular/platform-browser";
import {provideTranslateLoader, provideTranslateService, TranslateLoader, TranslateService} from "@ngx-translate/core";
import {
	SimConfigurationService,
	SimDeviceMappingService,
	SimHttpInterceptor,
	SimInjectableDataService,
	SimLocalizedResourceService,
	SimSystemExtendedService,
	SimUserExtendedService,
	SimUtilityService,
	SimWebpagePermissionService,
	SystemHub,
} from "@services";
import {firstValueFrom, from, Observable, of} from "rxjs";
import {DialogModule} from "./modules/dialog/dialog.module";
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions} from "@angular/material/tooltip";
import Hammer from "hammerjs";
import {SimErrorHandler} from "./shared/errorhandler/sim-error-handler";
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {EventEmitterService} from "@event-emitter";
import {DeviceService} from "./core/services/sim-services/device.service";
import {APP_BASE_HREF, DATE_PIPE_DEFAULT_OPTIONS, DatePipe} from "@angular/common";
import {BaseHubService} from "./api/hub-service-base.service";
import {provideCharts, withDefaultRegisterables} from "ng2-charts";
import {LogoComponent} from "./common/migration/shared-components/logo/logo.component";
import {SimButton} from "@common/ui/button/sim-button.component";
import {EnvironmentService} from "./common/migration/shared-components/environment-overlay/environment.service";
import {SideNavigationMenuComponent} from "@common/components/side-navigation-menu/side-navigation-menu.component";
import {provideMarkdown} from "ngx-markdown";
import {Translations as DebugTranslations} from "../sim.translations.barrel";
import {HeaderToolbarComponent} from "./core/components/sim-header-toolbar/sim-header-toolbar.component";
import {HeaderSidenavComponent} from "./core/components/sim-header-sidenav/sim-header-sidenav.component";
import {SystemActionsComponent} from "./common/migration/shared-components/system-actions/system-actions.component";
import {DateAdapter} from "@angular/material/core";
import {MatPaginatorIntl} from "@angular/material/paginator";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatPaginatorLocalization} from "./shared/types/mat-paginator-localization";
import {CustomDateAdapter} from "./common/migration/shared-components/event-calendar/event-calendar.component";
import {AddLeadingZeroPipe} from "./shared/shared-pipes/add-leading-zero.pipe";
import {FilterParticipantsPipe} from "@common/pipes/filter-participants.pipe";
import {TransformParticipantsToSimUsersPipe} from "@common/pipes/transform-participants-to-sim-users.pipe";
import {NgxEchartsModule} from "ngx-echarts";
import {CanvasRenderer} from "echarts/renderers";
import {BarChart, GraphChart, PieChart} from "echarts/charts";
import {GridComponent, LegendComponent, TooltipComponent} from "echarts/components";
import * as echarts from "echarts/core";
import {QuillModule} from "ngx-quill";
import {OnClickModule} from "@common/migration/shared-components/rich-text-editor/quill-image-popup-module/OnClickModule";

export const CUSTOM_SIM_TOOLTIP_OPTIONS: MatTooltipDefaultOptions = {
	showDelay: 0,
	hideDelay: 0,
	touchendHideDelay: 0,
	disableTooltipInteractivity: true,
};

echarts.use([CanvasRenderer, BarChart, PieChart, GraphChart, GridComponent, LegendComponent, TooltipComponent]);

function createMatPaginatorLocalization(translateService: TranslateService) {
	return new MatPaginatorLocalization(translateService);
}

export class SIMSTATION_HAMMER_JS_CONF extends HammerGestureConfig {
	buildHammer(element: HTMLElement) {
		const mc = new Hammer(element, {
			touchAction: "pan-y",
		});

		return mc;
	}
}

class SimTranslateLoader implements TranslateLoader {
	public constructor(protected localizedResourceService: SimLocalizedResourceService) {}

	getTranslation(lang: string): Observable<any> {
		if (lang === "SHOWKEYS") {
			return of(DebugTranslations);
		}

		return from(this.localizedResourceService.getAllLocalizedStrings(lang));
	}
}

const initializeAppProvider = provideAppInitializer(() =>
	initializeApp(
		inject(SimUserExtendedService),
		inject(SimConfigurationService),
		inject(SimDeviceMappingService),
		inject(SimWebpagePermissionService),
		inject(DeviceService),
		inject(TranslateService),
		inject(SimInjectableDataService),
		inject(SystemHub),
		inject(EnvironmentService),
	),
);

/**
 * Initializes app prior of App rendering
 */
async function initializeApp(
	userService: SimUserExtendedService,
	appConfig: SimConfigurationService,
	deviceMappingService: SimDeviceMappingService,
	webpagePermissionService: SimWebpagePermissionService,
	deviceService: DeviceService,
	translate: TranslateService,
	injectableDeviceDataService: SimInjectableDataService,
	systemHub: SystemHub,
	environmentService: EnvironmentService,
): Promise<unknown> {
	await appConfig.reloadConfig();

	// If the application runs in electron, wait for the device id to be set
	if (window.electron && !injectableDeviceDataService.clientData?.deviceId) {
		console.info("Electron detected - waiting for device id to be set");
		let tries = 10;
		while (tries-- > 0 && !injectableDeviceDataService.clientData?.deviceId) {
			console.info("waiting for device id to be set...");
			await SimUtilityService.delay(200);
		}
	}

	if (window.electron?.changeLaunchUrl) {
		window.electron.changeLaunchUrl(window.location.href);
	}

	environmentService.handleEnvironmentDeviceIdChanges();

	// the device id should be set before the websocket connection is configured
	await deviceService.registerDevice();
	await ensureHubIsConnected(systemHub);

	let userPromise = userService.init();
	let devicePromise = deviceMappingService.reloadClientProperties();
	let webPagePromise = webpagePermissionService.load();

	return Promise.all([userPromise, devicePromise, webPagePromise]).then(() => {
		return firstValueFrom(translate.get("GLOBAL_TEXT_SIMSTATION"));
	});
}

async function ensureHubIsConnected(hub: BaseHubService): Promise<boolean> {
	if (hub.isConnected()) {
		return true;
	} else {
		const result = await hub.connect();
		if (!result.success) {
			console.error("Could not connect system hub");
		}
		return result.success;
	}
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		DialogModule,
		LogoComponent,
		SimButton,
		SideNavigationMenuComponent,
		HeaderToolbarComponent,
		HeaderSidenavComponent,
		SystemActionsComponent,
		MatSidenavModule,
		NgxEchartsModule.forRoot({
			echarts,
		}),
		QuillModule.forRoot({
			customModules: [
				{
					implementation: OnClickModule,
					path: "modules/onclick",
				},
			],
		}),
	],
	providers: [
		provideZoneChangeDetection({
			eventCoalescing: true,
		}),
		SimConfigurationService,
		SimDeviceMappingService,
		SimUserExtendedService,
		EventEmitterService,
		initializeAppProvider,
		SimSystemExtendedService,
		provideTranslateService({
			loader: provideTranslateLoader(() => new SimTranslateLoader(inject(SimLocalizedResourceService))),
		}),
		provideMarkdown(),
		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: CUSTOM_SIM_TOOLTIP_OPTIONS,
		},
		{
			provide: HAMMER_GESTURE_CONFIG,
			useClass: SIMSTATION_HAMMER_JS_CONF,
		},
		{provide: ErrorHandler, useClass: SimErrorHandler},
		{provide: HTTP_INTERCEPTORS, useClass: SimHttpInterceptor, multi: true},
		{provide: APP_BASE_HREF, useValue: "/"},
		{
			provide: DATE_PIPE_DEFAULT_OPTIONS,
			useFactory: () => {
				const options = {timezone: SimUtilityService.getTimezoneOffsetString()};
				console.log("DATE_PIPE_DEFAULT_OPTIONS", options);
				return options;
			},
		},
		DatePipe,
		AddLeadingZeroPipe,
		FilterParticipantsPipe,
		TransformParticipantsToSimUsersPipe,
		{provide: DateAdapter, useClass: CustomDateAdapter},
		{
			provide: MatPaginatorIntl,
			deps: [TranslateService],
			useFactory: createMatPaginatorLocalization,
		},
		provideHttpClient(withInterceptorsFromDi()),
		provideCharts(withDefaultRegisterables()),
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
